import React, { useState, useEffect } from "react"; // Import useEffect
import styles from './.FilterBoxComponents.module.css';
import headcountStyles from './CompanyHeadcountFilter.module.css'; // Reusing styles
import { formatWithCommas } from "functions/fields"; // Assuming this utility exists

// Define the max clamp value
const MAX_RETAIL_LOCATIONS = 10_000_000;

const RetailLocationFilter = ({ retailLocations, setRetailLocations }) => {
    // Helper function to derive local state format from the retailLocations prop
    const getLocalStateFromProp = (propValue) => {
        // Default to [null, null] if prop isn't a valid array initially
        const validProp = Array.isArray(propValue) && propValue.length === 2 ? propValue : [null, null];
        return validProp.map(loc => (loc === null ? "" : String(loc)));
    };

    // Initialize local state using the helper, handling potential undefined/null initial prop
    const [localRetailLocations, setLocalRetailLocations] = useState(() => getLocalStateFromProp(retailLocations));

    // Effect to synchronize local state when the retailLocations prop changes from the parent
    useEffect(() => {
        const updatedLocalRetailLocations = getLocalStateFromProp(retailLocations);
        setLocalRetailLocations((prev) => {
            const currentLocal = Array.isArray(prev) ? prev : ["", ""];
            if (
                updatedLocalRetailLocations[0] === currentLocal[0] &&
                updatedLocalRetailLocations[1] === currentLocal[1]
            ) {
                return prev;
            }
            return updatedLocalRetailLocations;
        });
        // Dependency array: run this effect when the 'retailLocations' prop changes
    }, [retailLocations]);


    const handleChange = (e, index) => {
        const raw = e.target.value.replace(/,/g, "");
        const cleaned = raw.replace(/[^\d]/g, ""); // Keep only digits
        // Ensure localRetailLocations is treated as an array even if it becomes undefined momentarily
        const currentLocal = Array.isArray(localRetailLocations) ? localRetailLocations : ["", ""];
        const updated = [...currentLocal];
        updated[index] = cleaned;
        setLocalRetailLocations(updated);
    };

    // Updated handleBlur incorporating both fixes
    const handleBlur = () => {
        // Ensure localRetailLocations is treated as an array
        const currentLocal = Array.isArray(localRetailLocations) ? localRetailLocations : ["", ""];
        let [minStr, maxStr] = currentLocal;

        // Default to empty string if undefined/null before checking emptiness
        minStr = minStr ?? "";
        maxStr = maxStr ?? "";

        const min = minStr === "" ? null : Number(minStr);
        const max = maxStr === "" ? null : Number(maxStr);

        // Ensure no NaN makes it through (though unlikely with regex clean)
        let updatedMin = isNaN(min) ? null : min;
        let updatedMax = isNaN(max) ? null : max;

        // Auto-fix if max is lower than min (only if both are numbers)
        if (updatedMin !== null && updatedMax !== null && updatedMax < updatedMin) {
            updatedMax = updatedMin;
        }

        // Clamp max to the defined limit
        if (updatedMax !== null && updatedMax > MAX_RETAIL_LOCATIONS) {
            updatedMax = MAX_RETAIL_LOCATIONS;
        }

        // Final values after adjustments and clamping
        let finalMin = updatedMin;
        let finalMax = updatedMax;

        // --- FIX 2: Determine correct parent state format (empty array vs [null, null]) ---
        let nextParentState;
        if (finalMin === null && finalMax === null) {
            nextParentState = []; // Use empty array if both are null/cleared
        } else {
            nextParentState = [finalMin, finalMax];
        }

        // Get current parent state, default to [] if not an array for comparison
        const currentParentState = Array.isArray(retailLocations) ? retailLocations : [];

        // Check if the state actually needs to change by comparing length and elements
        let needsUpdate = false;
        if (nextParentState.length !== currentParentState.length) {
            needsUpdate = true;
        } else if (nextParentState.length === 2) {
            // Only compare elements if both arrays have length 2
            if (nextParentState[0] !== currentParentState[0] || nextParentState[1] !== currentParentState[1]) {
                needsUpdate = true;
            }
        }
        // If both lengths are 0, needsUpdate remains false (no change needed)

        // Update parent state only if the calculated state is different
        if (needsUpdate) {
            setRetailLocations(nextParentState);
        }
        // --- END FIX 2 ---


        // Update local state based on the final validated/adjusted values, ensuring strings
        const nextLocalState = [
            finalMin === null ? "" : String(finalMin),
            finalMax === null ? "" : String(finalMax)
        ];
        // Only update local state if it differs, prevents re-render if blur didn't change effective value
        if(nextLocalState[0] !== currentLocal[0] || nextLocalState[1] !== currentLocal[1]) {
           setLocalRetailLocations(nextLocalState);
        }
    };

    // Ensure localRetailLocations is always an array for rendering
    const displayRetailLocations = Array.isArray(localRetailLocations) ? localRetailLocations : ["", ""];

    return (
        <div className={styles.accordionContent}>
            <label>Retail locations is between:</label>
            {/* Using headcountStyles as specified in original code */}
            <div className={headcountStyles.employeeHeadcountRange}>
                <div className={headcountStyles.headcountInputContainer}>
                    <input
                        type="text"
                        // Use displayRetailLocations which is guaranteed to be an array
                        value={displayRetailLocations[0] === "" ? "" : formatWithCommas(displayRetailLocations[0])}
                        onChange={(e) => handleChange(e, 0)}
                        onBlur={handleBlur}
                        placeholder="No min"
                    />
                    <label>Min</label>
                </div>
                <div className={headcountStyles.headcountInputContainer}>
                    <input
                        type="text"
                        // Use displayRetailLocations which is guaranteed to be an array
                        value={displayRetailLocations[1] === "" ? "" : formatWithCommas(displayRetailLocations[1])}
                        onChange={(e) => handleChange(e, 1)}
                        onBlur={handleBlur}
                        placeholder="No max"
                    />
                    <label>Max</label>
                </div>
            </div>
        </div>
    );
};

export default RetailLocationFilter;